import React, { useState, useEffect } from 'react';
import { API, authFetch } from '../utils/api';
import ExtraServiceChipTable from './ExtraServiceChipTable';

// ═══ 요청 상세 — 부가서비스 편집 ═══
// 대행사는 칩으로 카탈로그 항목을 담고, 표에서 금액·메모를 고쳐 저장할 수 있다. 카탈로그에 없는 항목은
// 칩 줄 끝의 "직접 입력" 으로 이름 입력칸이 있는 자유 행을 추가한다(serviceId 없음).
// readOnly(수출업체 측)는 이름/금액(showFee 일 때만)만 보여주는 목록으로 그린다.
// GET/PUT /cancel-requests/:id/services 가 아직 배포 전이면(404/405) 조용히 숨는다.
function RequestServicesEditor({ requestId, agencyId, showFee, readOnly, showToast, onSaved, onRowsChange }) {
  var toast = showToast || function(){};
  var [rows, setRows] = useState([]);
  var [catalog, setCatalog] = useState([]);
  var [unavailable, setUnavailable] = useState(false);
  var [saving, setSaving] = useState(false);

  // afterSave — 저장 직후 재조회일 때만 onSaved(rows) 통지 (정산 항목의 파생 금액을 부모가 갱신).
  // onRowsChange — 로드/편집 시마다(저장 전 포함) 통지 — 부모의 저장 전 실시간 미리보기 갱신용 (B-1).
  var load = function(afterSave){
    if(!requestId){ setRows([]); if(onRowsChange) onRowsChange([]); return; }
    authFetch(API+'/cancel-requests/'+requestId+'/services').then(function(r){
      if(r.status===404||r.status===405){ setUnavailable(true); return null; }
      if(!r.ok) throw new Error('HTTP '+r.status);
      return r.json();
    }).then(function(d){
      if(d){
        var list=Array.isArray(d)?d:[];
        setRows(list); setUnavailable(false);
        if(onRowsChange) onRowsChange(list);
        if(afterSave && onSaved) onSaved(list);
      }
    }).catch(function(){ setRows([]); });
  };
  useEffect(function(){ load(); }, [requestId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(function(){
    if(!agencyId || readOnly){ setCatalog([]); return; }
    authFetch(API+'/cancel-agencies/'+agencyId+'/extra-services').then(function(r){ if(!r.ok) return null; return r.json(); })
      .then(function(d){ if(d) setCatalog(Array.isArray(d)?d:[]); }).catch(function(){});
  }, [agencyId, readOnly]);

  if(unavailable) return null;

  var setRow = function(idx, key, v){
    var next = rows.map(function(row,i){ if(i!==idx) return row; var n=Object.assign({},row); n[key]=v; return n; });
    setRows(next); if(onRowsChange) onRowsChange(next);
  };
  var removeRow = function(idx){
    var next = rows.filter(function(_,i){return i!==idx;});
    setRows(next); if(onRowsChange) onRowsChange(next);
  };
  var toggleChip = function(c){
    var existing = rows.filter(function(r){return r.serviceId===c.id;})[0];
    // 카탈로그에 defaultFee 가 없으면(공개 목록은 {id,name} 만) 0 표시 — 서버가 스냅샷 시 기본값을 채운다.
    var next = existing ? rows.filter(function(r){return r.serviceId!==c.id;})
      : rows.concat([{serviceId:c.id, name:c.name, fee:c.defaultFee||0, memo:'', kind:c.kind}]);
    setRows(next); if(onRowsChange) onRowsChange(next);
  };
  var addFree = function(){
    var next = rows.concat([{name:'', fee:0, memo:''}]);
    setRows(next); if(onRowsChange) onRowsChange(next);
  };
  var save = function(){
    setSaving(true);
    var body = { services: rows.map(function(r){
      var o = {};
      if(r.serviceId) o.serviceId = r.serviceId; else o.name = r.name;
      o.fee = parseInt(r.fee,10)||0; o.memo = r.memo||'';
      if(r.kind) o.kind = r.kind;
      return o;
    }) };
    authFetch(API+'/cancel-requests/'+requestId+'/services', {method:'PUT', headers:{'Content-Type':'application/json'}, body:JSON.stringify(body)})
      .then(function(r){ return r.json().catch(function(){ return {}; }); })
      .then(function(d){
        setSaving(false);
        if(Array.isArray(d)){ setRows(d); toast('부가서비스 저장 완료'); if(onRowsChange) onRowsChange(d); if(onSaved) onSaved(d); return; }
        if(d && d.success===false){ toast(d.error||'저장 실패','err'); return; }
        toast('부가서비스 저장 완료'); load(true);
      }).catch(function(){ setSaving(false); toast('서버 오류','err'); });
  };

  var selectedIds = new Set(rows.filter(function(r){return r.serviceId;}).map(function(r){return r.serviceId;}));
  var tableRows = rows.map(function(r, idx){ return { key: r.id!=null?r.id:('new'+idx), name: r.name, fee: r.fee, memo: r.memo, editableName: !r.serviceId && r.kind!=='reissue', kind: r.kind }; });

  return (<div style={{marginTop:14}}>
    <label style={{display:'block',fontSize:12,fontWeight:600,color:'#374151',marginBottom:6}}>부가서비스</label>
    <ExtraServiceChipTable
      chips={catalog}
      selectedIds={selectedIds}
      onToggleChip={toggleChip}
      extraChip={!readOnly?{label:'직접 입력', onClick:addFree}:undefined}
      rows={tableRows}
      showFee={showFee}
      readOnly={readOnly}
      onFeeChange={function(idx,v){setRow(idx,'fee',v);}}
      onMemoChange={function(idx,v){setRow(idx,'memo',v);}}
      onNameChange={function(idx,v){setRow(idx,'name',v);}}
      onRemove={removeRow}
      emptyText={readOnly?'등록된 부가서비스가 없습니다.':undefined}
    />
    {!readOnly && (<div style={{display:'flex',marginTop:8}}>
      <div style={{flex:1}} />
      <button type="button" className="btn btn-blue btn-sm" disabled={saving} onClick={save}>저장</button>
    </div>)}
  </div>);
}

export default RequestServicesEditor;
